import { Input, Switch, Tooltip, Typography } from 'antd';
import type { ScreenDiagnostic } from '@/api/screens';
import type { LocalizedText } from './model';
import { errorAt } from './diagnostics';

const { Text } = Typography;

/**
 * One piece of applicant-facing text: English plus each other language the journey declares, or a
 * content key from the hub's ladder. Clearing everything clears the text, and the room's own wording
 * comes back. What is legal is the gate's answer, shown on the input.
 */
export function LocalizedField({
  label,
  hint,
  value,
  locales,
  onChange,
  diagnostics,
  path,
  multiline,
  placeholder,
}: {
  label: string;
  hint?: string;
  value: LocalizedText | undefined;
  locales: string[];
  onChange: (next: LocalizedText | undefined) => void;
  diagnostics: ScreenDiagnostic[];
  /** The JSON pointer of this text in the definition. */
  path: string;
  multiline?: boolean;
  placeholder?: string;
}) {
  const usesKey = value?.key !== undefined;
  const setLocale = (locale: string, text: string) => {
    const next = { ...(value ?? {}) } as Record<string, string>;
    if (text) next[locale] = text;
    else delete next[locale];
    onChange(Object.keys(next).length > 0 ? (next as LocalizedText) : undefined);
  };
  const languages = ['en', ...locales.filter((l) => l !== 'en')];
  const Field = multiline ? Input.TextArea : Input;
  return (
    <div>
      <div style={{ fontSize: 12, marginBottom: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
        {label}
        {hint && (
          <Tooltip title={hint}>
            <Text type="secondary" style={{ fontSize: 11, cursor: 'help' }}>
              ⓘ
            </Text>
          </Tooltip>
        )}
        <span style={{ marginInlineStart: 'auto', display: 'inline-flex', gap: 4, alignItems: 'center' }}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            content key
          </Text>
          <Switch
            size="small"
            checked={usesKey}
            aria-label={`${label}: use a content key`}
            onChange={(on) => onChange(on ? { key: '' } : undefined)}
          />
        </span>
      </div>
      {usesKey ? (
        <Input
          className="ol-mono"
          value={value?.key ?? ''}
          placeholder="hub.content.key"
          aria-label={`${label} content key`}
          status={errorAt(diagnostics, `${path}/key`) ? 'error' : undefined}
          onChange={(e) => onChange({ key: e.target.value })}
        />
      ) : (
        languages.map((locale) => (
          <div key={locale} style={{ display: 'flex', gap: 6, alignItems: 'baseline', marginBottom: 4 }}>
            {languages.length > 1 && (
              <Text type="secondary" className="ol-mono" style={{ fontSize: 11, width: 18 }}>
                {locale}
              </Text>
            )}
            <Field
              value={(value as Record<string, string> | undefined)?.[locale] ?? ''}
              placeholder={locale === 'en' ? placeholder : undefined}
              aria-label={`${label} (${locale})`}
              dir={locale === 'ar' ? 'rtl' : undefined}
              status={errorAt(diagnostics, `${path}/${locale}`) ? 'error' : undefined}
              onChange={(e) => setLocale(locale, e.target.value)}
            />
          </div>
        ))
      )}
    </div>
  );
}
